import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { createContext, useContext } from 'react'
import { Card, Box, Paper, Tabs, Tab } from '@mui/material'
import PropTypes from 'prop-types'
import { BaseWrapper } from '../BaseWrapper'
import { getString } from '../../helper/util'
import StatusLabel from '../_v2/label/StatusChip'
import { constants } from '../../Constants/constants'
import '../../styles/features/payment/uploadPayment.scss'

// ─── Context ────────────────────────────────────────────────────────────────
const EditCreateContext = createContext({})
const useEditCreateContext = () => useContext(EditCreateContext)

// ─── Root ────────────────────────────────────────────────────────────────────
export function EditCreateDisplayV2({ children, activeTab, onChangeTab }) {
  const lang = useSelector((state) => state.lang.ui.lang)
  const theme = useSelector((state) => state.theme.ui.theme)

  return (
    <EditCreateContext.Provider value={{ activeTab, onChangeTab, lang, theme }}>
      <BaseWrapper>
        <Card>{children}</Card>
      </BaseWrapper>
    </EditCreateContext.Provider>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────────
EditCreateDisplayV2.Header = function Header({ resource, display, code, mapName, mapCode, status }) {
  const { lang, theme } = useEditCreateContext()

  if (display === 'none') return null

  return (
    <Box className="margin-left-16" sx={{ pt: 2 }}>
      {mapCode ? (
        mapName ? (
          <>
            <div className="sub-title">
              {getString(lang, mapName.resource, mapName.key, theme)}:{' '}
              <span className="code-title">{mapCode}</span>
            </div>
            <div className="sub-title">
              {getString(lang, resource, `${display}Title`, theme)}:{' '}
              <span className="code-title">{code}</span>
            </div>
          </>
        ) : (
          <>
            <div className="code-title">{mapCode}</div>
            <div className="sub-title">
              {getString(lang, resource, `${display}Title`, theme)}:{' '}
              <span className="code-title">{code}</span>
            </div>
          </>
        )
      ) : (
        <div className="sub-title">
          {getString(lang, resource, `${display}Title`, theme)}:{' '}
          <span className="code-title">{code}</span>
          {status && <StatusLabel resource={resource} keyName={status} />}
        </div>
      )}
    </Box>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
EditCreateDisplayV2.Tabs = function TabsBar({ tabs }) {
  const { activeTab, onChangeTab, lang, theme } = useEditCreateContext()
  if (!tabs?.length) return null

  return (
    <Paper position="static" color="default">
      <Tabs value={Number(activeTab)} onChange={onChangeTab} aria-label="edit create tabs">
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            // 👇 prefer labelText (static string) over resource lookup
            label={tab.labelText ?? getString(lang, tab.label?.resource ?? '', tab.label?.key ?? '', theme)}
            id={`tab-${index}`}
            aria-controls={`tabpanel-${index}`}
          />
        ))}
      </Tabs>
    </Paper>
  )
}

// ─── TabPanel ────────────────────────────────────────────────────────────────
EditCreateDisplayV2.TabPanel = function TabPanel({ index, children }) {
  const { activeTab } = useEditCreateContext()
  const isActive = Number(activeTab) === index

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {isActive && <Box p={4}>{children}</Box>}
    </div>
  )
}

// ─── Body (no tabs) ───────────────────────────────────────────────────────────
EditCreateDisplayV2.Body = function Body({ children }) {
  return <div className="card-wrapper">{children}</div>
}

// ─── Footer ───────────────────────────────────────────────────────────────────
EditCreateDisplayV2.Footer = function Footer({ children }) {
  if (!children) return null
  return (
    <Box className="edit-footer-wrapper padding-16" sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2 }}>
      {children}
    </Box>
  )
}

// ─── PropTypes ───────────────────────────────────────────────────────────────
EditCreateDisplayV2.propTypes = {
  activeTab: PropTypes.number,
  onChangeTab: PropTypes.func,
  children: PropTypes.node,
}

EditCreateDisplayV2.Header.propTypes = {
  resource: PropTypes.string,
  display: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mapName: PropTypes.shape({ resource: PropTypes.string, key: PropTypes.string }),
  mapCode: PropTypes.string,
  status: PropTypes.string,
}

// ─── EditCreatePage ───────────────────────────────────────────────────────────
export function EditCreatePage({ savedData, handleChange, errors, handleCancel, handleSave }) {
  const { tab } = useParams()
  const history = useHistory()

  const handleChangeTab = (e, newValue) => {
    history.push(location.pathname.replace(/\/\d+$/, `/${newValue}`))
  }

  return (
    <EditCreateDisplayV2 activeTab={Number(tab)} onChangeTab={handleChangeTab}>
      <EditCreateDisplayV2.Header
        resource="appVersionPage"
        display="edit"
        code={savedData.appVersion}
        status={savedData.status}
      />

      <EditCreateDisplayV2.Tabs
        tabs={[
          { label: { resource: 'appVersionPage', key: 'basicInfo' } },
          { label: { resource: 'appVersionPage', key: 'releaseNote' } },
        ]}
      />

      <EditCreateDisplayV2.TabPanel index={0}>
        <AppVersionForm data={savedData} onChange={handleChange} errors={errors} />
      </EditCreateDisplayV2.TabPanel>

      <EditCreateDisplayV2.TabPanel index={1}>
        <ReleaseNoteForm data={savedData} onChange={handleChange} />
      </EditCreateDisplayV2.TabPanel>

      <EditCreateDisplayV2.Footer>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </EditCreateDisplayV2.Footer>
    </EditCreateDisplayV2>
  )
}
